import React from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';

import {
  Flex,
  Span,
  Table,
  Button,
  Tooltip,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  Pagination,
  TableActions,
  OrderableTableRow,
  useOrderableTableReducer
} from '@sopranodesign/pulse-components';
import { PermissionFilter } from '@sopranodesign/pulse-auth';

const JiraProjectsList = ({
  projects = [],
  settings,
  onDisconnect,
  onView,
  onResync,
  onDelete,
  onAddProjects,
  onResyncAll,
  isAddProjectsLoading,
  pagination = {}
}) => {
  const { meta } = pagination;
  const order = useOrderableTableReducer({ id: 'jira-projects' });

  const sortedProjects = React.useMemo(() => {
    if (!order.params.order) { return projects; }
    const sorted = [...projects].sort((a, b) => {
      const dir = order.params.direction === 'descending' ? -1 : 1;
      const key = order.params.order;
      if (a[key] < b[key]) { return -1 * dir; }
      if (a[key] > b[key]) { return 1 * dir; }
      return 0;
    });
    return sorted;
  }, [projects, order.params]);

  return (
    <Flex col>
      <Flex border xScroll>
        <Table>
          <TableHead>
            <OrderableTableRow
              rows={[
                { label: 'Project', sort: 'project_name' },
                { label: 'Last Sync', sort: 'last_sync_at' },
                null
              ]}
              {...order.props}
            />
          </TableHead>
          <TableBody>
            {sortedProjects.map(project => (
              <TableRow key={project.id}>
                <TableCell>{project.project_name}</TableCell>
                <TableCell>{project.last_sync_at ? dayjs(project.last_sync_at).format('YYYY-MM-DD HH:mm:ss') : '-'}</TableCell>
                <TableActions>
                  <Button
                    variant="secondary"
                    icon="eye"
                    onClick={() => onView(project.id)}
                  >
                    View
                  </Button>

                  <PermissionFilter can="edit generative ai resource">
                    <Button
                      variant="secondary"
                      icon="refresh"
                      onClick={() => onResync(project)}
                    >
                      Resync
                    </Button>

                    <Button
                      variant="secondary"
                      icon="trash"
                      colour="danger"
                      onClick={() => onDelete(project)}
                    >
                      Delete
                    </Button>
                  </PermissionFilter>
                </TableActions>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Flex>

      {typeof meta.current_page === 'number' && (
        <Pagination pagination={meta} {...pagination.props} />
      )}

      <PermissionFilter can="edit generative ai resource">
        <Flex center wrap="true" gap="2" right mt>
          <Button
            ml
            icon="plus"
            variant="secondary"
            onClick={onAddProjects}
            loading={isAddProjectsLoading}
          >
            Add Projects
          </Button>
          <Button
            ml
            icon="refresh"
            variant="secondary"
            onClick={onResyncAll}
          >
            Sync All
          </Button>
          <Button
            ml
            icon="trash"
            variant="secondary"
            colour="danger"
            onClick={onDisconnect}
          >
            Disconnect
          </Button>
        </Flex>
      </PermissionFilter>

      <Flex center right mt>
        <Span light tinyText>
          <Tooltip
            position="RIGHT"
            tooltip={<Tooltip.Content>{dayjs(settings?.updated_at).format('YYYY-MM-DD HH:mm:ss')}</Tooltip.Content>}
          >
            Last sync {dayjs(settings?.updated_at).from(dayjs())}
          </Tooltip>
        </Span>
      </Flex>
    </Flex>
  );
};

JiraProjectsList.propTypes = {
  projects: PropTypes.array.isRequired,
  settings: PropTypes.object,
  onDisconnect: PropTypes.func.isRequired,
  onView: PropTypes.func.isRequired,
  onResync: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onAddProjects: PropTypes.func.isRequired,
  onResyncAll: PropTypes.func.isRequired,
  isAddProjectsLoading: PropTypes.bool,
  pagination: PropTypes.object
};

export default JiraProjectsList;
